import { People, PriceCheck, Restore, Settings, ThumbUp } from '@mui/icons-material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CloseIcon from '@mui/icons-material/Close';
import ListIcon from '@mui/icons-material/List';
import { Box, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText, SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { Link } from 'react-router-dom';
import { useRefEffect, useUserPermissions } from 'shared/hooks';
import { formatHtmlId } from 'shared/utility';

interface IDrawerRoute {
    path: string;
    displayText: string;
    Icon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
        muiName: string;
    };
    isVisible: boolean;
}

// admins can see all the links
// some links could only be seeing by customerSalesRep or Admin

interface NavigationDrawerProps {
    drawerOnCloseHandler: () => void;
    isDrawerOpen: boolean;
    drawerTopMargin: number;
}

export function NavigationDrawer({ drawerOnCloseHandler, isDrawerOpen, drawerTopMargin }: NavigationDrawerProps) {
    const {
        isPageProductManagementAllowed,
        isPageActivePriceManagementAllowed,
        isPageFuturePriceManagementAllowed,
        isPageSettingsAllowed
    } =
        useUserPermissions();

    const drawerRoutes: IDrawerRoute[] = [
        {
            path: '/distributors',
            displayText: 'Distributor Management',
            Icon: People,
            isVisible: true,
        },
        { path: '/approvals', displayText: 'Approvals', Icon: ThumbUp, isVisible: true },
        {
            path: '/priceHistory',
            displayText: 'Price History',
            Icon: Restore,
            isVisible: true,
        },
        {
            path: '/productManagement',
            displayText: 'Product Management',
            Icon: ListIcon,
            isVisible: isPageProductManagementAllowed,
        },
        {
            path: '/activePriceManagement',
            displayText: 'Active Price Management',
            Icon: AttachMoneyIcon,
            isVisible: isPageActivePriceManagementAllowed,
        },
        {
            path: '/futurePriceManagement/marketPrices',
            displayText: 'Future Price Management',
            Icon: PriceCheck,
            isVisible: isPageFuturePriceManagementAllowed,
        },
        {
            path: '/settings',
            displayText: 'Settings',
            Icon: Settings,
            isVisible: isPageSettingsAllowed,
        },
    ];

    const drawerRef = useRefEffect(
        (node) => {
            const muiDrawerBackdrop = node.children.item(0) as HTMLElement;
            muiDrawerBackdrop.style.top = `${drawerTopMargin}px`;

            const muiDrawerPaper = node.children.item(2) as HTMLElement;
            muiDrawerPaper.style.top = `${drawerTopMargin}px`;
        },
        [drawerTopMargin]
    );

    return (
        <Drawer
            anchor={'left'}
            open={isDrawerOpen}
            onClose={drawerOnCloseHandler}
            sx={{ top: `${drawerTopMargin}px` }}
            ref={drawerRef}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row-reverse',
                    }}
                >
                    <IconButton
                        id={'navigation-drawer-close'}
                        aria-label='close-navigation'
                        size='small'
                        onClick={drawerOnCloseHandler}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <List>
                    {drawerRoutes
                        .filter((drawerRoute) => drawerRoute.isVisible)
                        .map(({ path, displayText, Icon }) => (
                            <Link
                                id={`navigation-drawer-link-${formatHtmlId(path)}`}
                                to={path}
                                key={path}
                                onClick={drawerOnCloseHandler}
                            >
                                <ListItem>
                                    <ListItemIcon>
                                        <Icon />
                                    </ListItemIcon>
                                    <ListItemText>{displayText}</ListItemText>
                                </ListItem>
                            </Link>
                        ))}
                </List>
            </Box>
        </Drawer>
    );
}
